import { CountryCode, getCountryBankGroup } from '@koda/koda-components/utils/countries';
import { z } from 'zod';
import { locales } from '../../../../../i18n/constants';
import { isDanishFlowCountry } from '../flow';
import { MEMBER_VALIDATION_CONTENT_PATH, validators } from './validators';

export const MemberSchema = z.object({
  BankRegNumber: z.string(),
  BankAccountNumber: z.string(),
  AbaRoutingNumber: z.string(),
  SwiftBicCode: z.string(),
  IbanCode: z.string(),
  BankAddress: z.string(),
  BankName: z.string(),
  Cpr: z.string(),
  CVRNo: z.string(),
  CompanyName: z.string(),
  PublisherName: z.string(),
  Address1: validators.address(),
  Address2: z.string(),
  Zip: validators.zipCode(),
  City: validators.city(),
  MobilePhone: z.string(),
  Phone: validators.phone(),
  Email: validators.email().transform(value => value.toLowerCase()),
  LanguageCode: z.enum(locales),
  NewsletterDa: z.boolean(),
  NewsletterFo: z.boolean(),
  NewsletterGl: z.boolean(),
  UserType: z.string(),
  Amount: z.number(),
  TransactionId: z.string(),
  OrderId: z.string(),
  TIN: z.string(),
  Vaergenavn: z.string(),
  Vaergeemail: validators.guardianEmail(),
  Vaergetelefon: z.string(),
  VaergeCpr: z.string(),
  Name: validators.name(),
  CountryCode: validators.countryCode(),
  ArtistName: z.string().optional(),
  BandName: z.string().optional(),
  Fodselsdato: validators.birthdate(),
  HasDanishCpr: validators.hasDanishCpr(),
  Consent: validators.consent(),
  CriiptoValidationToken: z.string(),

  // Values not in the form
  didAcceptTerms: z.boolean(),
  lockedFields: z.object({
    Name: z.boolean(),
    Country: z.boolean(),
    Fodselsdato: z.boolean(),
    HasDanishCpr: z.boolean(),
  }),
});

export const PersonalInfoSchema = MemberSchema.pick({
  Name: true,
  ArtistName: true,
  BandName: true,
  CountryCode: true,
  Fodselsdato: true,
  HasDanishCpr: true,
}).superRefine((data, ctx) => {
  if (!isDanishFlowCountry(data.CountryCode) && data.HasDanishCpr === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['HasDanishCpr'],
      message: `${MEMBER_VALIDATION_CONTENT_PATH}.hasDanishCpr.required`,
    });
  }
});

export const ContactInfoSchema = MemberSchema.pick({
  Phone: true,
  Email: true,
  Consent: true,
});

export const AddressInfoSchema = MemberSchema.pick({
  Address1: true,
  Address2: true,
  Zip: true,
  City: true,
  CountryCode: true,
}).superRefine((data, ctx) => {
  if (isDanishFlowCountry(data.CountryCode)) {
    if (!/^\d{4}$/.test(data.Zip)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['Zip'],
        message: `${MEMBER_VALIDATION_CONTENT_PATH}.zipCode.maxFourDigits`,
      });
    }
  }
});

type BankRequiredField =
  | 'TIN'
  | 'BankAddress'
  | 'BankName'
  | 'SwiftBicCode'
  | 'AbaRoutingNumber'
  | 'BankAccountNumber'
  | 'IbanCode'
  | 'BankRegNumber';

const bankFieldRequiredMessage: Record<BankRequiredField, string> = {
  TIN: `${MEMBER_VALIDATION_CONTENT_PATH}.bank.tinRequired`,
  BankAddress: `${MEMBER_VALIDATION_CONTENT_PATH}.bank.countryRequired`,
  BankName: `${MEMBER_VALIDATION_CONTENT_PATH}.bank.bankNameRequired`,
  SwiftBicCode: `${MEMBER_VALIDATION_CONTENT_PATH}.bank.bicRequired`,
  AbaRoutingNumber: `${MEMBER_VALIDATION_CONTENT_PATH}.bank.abaRoutingRequired`,
  BankAccountNumber: `${MEMBER_VALIDATION_CONTENT_PATH}.bank.bankAccountNumberRequired`,
  IbanCode: `${MEMBER_VALIDATION_CONTENT_PATH}.bank.ibanRequired`,
  BankRegNumber: `${MEMBER_VALIDATION_CONTENT_PATH}.bank.bankRegNumberRequired`,
};

const requiredBankFieldsByBankGroup = {
  denmark: ['BankAddress', 'BankAccountNumber', 'BankRegNumber'],
  europe: ['BankAddress', 'BankName', 'SwiftBicCode', 'IbanCode'],
  usa: ['BankAddress', 'BankAccountNumber', 'BankName', 'SwiftBicCode', 'AbaRoutingNumber'],
  other: ['BankAddress', 'BankName', 'SwiftBicCode', 'BankAccountNumber'],
} as const satisfies Record<'denmark' | 'europe' | 'usa' | 'other', BankRequiredField[]>;

const createBankInfoSchema = ({ requireTin }: { requireTin: boolean }) =>
  MemberSchema.pick({
    CountryCode: true,
    TIN: true,
    BankAddress: true,
    BankName: true,
    BankRegNumber: true,
    AbaRoutingNumber: true,
    BankAccountNumber: true,
    IbanCode: true,
    SwiftBicCode: true,
  }).superRefine((data, ctx) => {
    const addRequiredIssue = (field: BankRequiredField) => {
      const value = data[field];
      const message = bankFieldRequiredMessage[field];
      if (!validators.bankRequiredString().safeParse(value).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message,
        });
      }
    };

    const bankGroup = getCountryBankGroup((data.BankAddress || data.CountryCode) as CountryCode);
    const requiredFields: BankRequiredField[] = [...requiredBankFieldsByBankGroup[bankGroup]];

    if (requireTin) {
      requiredFields.push('TIN');
    }

    for (const requiredField of requiredFields) {
      addRequiredIssue(requiredField);
    }

    if (data.SwiftBicCode.trim().length > 0) {
      const bicLength = data.SwiftBicCode.trim().length;
      if (bicLength < 8 || bicLength > 11) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['SwiftBicCode'],
          message: `${MEMBER_VALIDATION_CONTENT_PATH}.bank.bicLength`,
        });
      }
    }

    if (bankGroup === 'denmark') {
      const accountNumber = data.BankAccountNumber.trim();
      if (accountNumber.length > 0 && !/^\d{10}$/.test(accountNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['BankAccountNumber'],
          message: `${MEMBER_VALIDATION_CONTENT_PATH}.bank.bankAccountNumberLength`,
        });
      }

      const bankRegNumber = data.BankRegNumber.trim();
      if (bankRegNumber.length > 0 && !/^\d{4}$/.test(bankRegNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['BankRegNumber'],
          message: `${MEMBER_VALIDATION_CONTENT_PATH}.bank.bankRegNumberLength`,
        });
      }
    }
  });

export const BankInfoSchema = createBankInfoSchema({ requireTin: false });
export const BankInfoSchemaWithRequiredTin = createBankInfoSchema({ requireTin: true });

export const MitIdSchema = z.object({
  Cpr: validators.cpr(),
  CriiptoValidationToken: z.string(),
});

export const ConfirmSchema = MemberSchema.pick({
  Name: true,
  ArtistName: true,
  BandName: true,
  CountryCode: true,
  Phone: true,
  Email: true,
  Consent: true,
  NewsletterGl: true,
  NewsletterDa: true,
  NewsletterFo: true,
  Address1: true,
  Zip: true,
  City: true,
});

export const ConfirmSchemaWithAcceptTerms = MemberSchema.pick({
  Name: true,
  ArtistName: true,
  BandName: true,
  CountryCode: true,
  Phone: true,
  Email: true,
  Consent: true,
  NewsletterGl: true,
  NewsletterDa: true,
  NewsletterFo: true,
  Address1: true,
  Zip: true,
  City: true,
  didAcceptTerms: true,
}).extend({
  didAcceptTerms: validators.termsAcceptance(),
});
